import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Shield, Globe, FileText, Clock, CheckCircle2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const GEO_COLORS = {
  Global: "#36A9E1",
  "United States": "#4ADE80",
  Brazil: "#F59E0B",
  "European Union": "#A78BFA",
};

const STATUS_BADGES = {
  released: { label: "Released", color: "#4ADE80", bg: "rgba(74,222,128,0.1)" },
  "Under Review": { label: "Under Review", color: "#F59E0B", bg: "rgba(245,158,11,0.1)" },
  obsolete: { label: "Obsolete", color: "#6B7280", bg: "rgba(107,114,128,0.1)" },
};

export default function GovernanceLibraryPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [dueForReview, setDueForReview] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/governance-library`, { withCredentials: true });
      setDocs(res.data.documents || []);
      setDueForReview(res.data.due_for_review || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#36A9E1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="governance-library">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Governance Library</h1>
          <p className="text-sm text-[#8B95A5] mt-1">
            {docs.length} governance documents | {dueForReview} due for quarterly review
          </p>
        </div>
        {dueForReview > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.2)]">
            <AlertTriangle size={14} className="text-[#F59E0B]" />
            <span className="text-xs text-[#F59E0B]">{dueForReview} due for review</span>
          </div>
        )}
      </div>

      <div className="grid gap-3">
        {docs.map((doc) => {
          const status = STATUS_BADGES[doc.status] || STATUS_BADGES.released;
          const geoColor = GEO_COLORS[doc.geography] || "#8B95A5";

          return (
            <div
              key={doc.doc_id}
              onClick={() => navigate(`/doc-control`)}
              className="flex items-center gap-4 p-4 bg-[#1A1D27] border border-[rgba(255,255,255,0.06)] rounded-lg cursor-pointer hover:border-[rgba(54,169,225,0.3)] transition-colors"
              data-testid={`gov-doc-${doc.doc_id}`}
            >
              <div className="shrink-0">
                <Shield size={20} style={{ color: geoColor }} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white truncate">{doc.name}</span>
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{ color: status.color, background: status.bg }}
                  >
                    {status.label}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1 text-[11px]" style={{ color: geoColor }}>
                    <Globe size={10} /> {doc.geography}
                  </span>
                  <span className="text-[11px] text-[#8B95A5]">
                    <FileText size={10} className="inline mr-1" />v{doc.version} r{doc.revision}
                  </span>
                  <span className="text-[11px] text-[#8B95A5]">
                    <Clock size={10} className="inline mr-1" />
                    {doc.effective_date || "N/A"}
                  </span>
                </div>
              </div>

              <div className="shrink-0">
                <CheckCircle2 size={16} className="text-[#4ADE80]" />
              </div>
            </div>
          );
        })}
      </div>

      {docs.length === 0 && (
        <div className="text-center py-16 text-[#8B95A5]">
          <Shield size={32} className="mx-auto mb-3 opacity-30" />
          <p>No governance documents found.</p>
        </div>
      )}
    </div>
  );
}
